/**
 * Prevent exposed headings in the main content from skipping semantic levels.
 *
 * Theme text blocks store visual typography separately from their HTML tag.
 * This guard preserves those tags and styles, but supplies the corrected ARIA
 * level when content configured in the Theme Editor skips a heading level.
 */
(() => {
  const main = document.getElementById('MainContent');

  if (!main) return;

  const headingSelector = 'h1, h2, h3, h4, h5, h6, [role="heading"][aria-level]';
  const noValue = '__none__';
  let frameId;

  function isExposed(heading) {
    if (heading.closest('[hidden], [aria-hidden="true"], [inert]')) return false;
    const style = window.getComputedStyle(heading);
    return style.display !== 'none' && style.visibility !== 'hidden' && heading.getClientRects().length > 0;
  }

  function getHeadingLevel(heading) {
    const explicitLevel = Number.parseInt(heading.getAttribute('aria-level'), 10);
    if (Number.isInteger(explicitLevel) && explicitLevel > 0) return explicitLevel;

    const nativeLevel = Number.parseInt(heading.tagName.slice(1), 10);
    return Number.isInteger(nativeLevel) ? nativeLevel : null;
  }

  function restoreAdjustedHeadings() {
    main.querySelectorAll('[data-heading-order-adjusted]').forEach((heading) => {
      const originalRole = heading.dataset.headingOrderOriginalRole;
      const originalLevel = heading.dataset.headingOrderOriginalLevel;

      if (originalRole === noValue) heading.removeAttribute('role');
      else heading.setAttribute('role', originalRole);

      if (originalLevel === noValue) heading.removeAttribute('aria-level');
      else heading.setAttribute('aria-level', originalLevel);

      delete heading.dataset.headingOrderAdjusted;
      delete heading.dataset.headingOrderOriginalRole;
      delete heading.dataset.headingOrderOriginalLevel;
    });
  }

  function normalizeHeadingOrder() {
    restoreAdjustedHeadings();

    // Seed from the document's h1 so the first heading inside main is also
    // corrected if it skips (previously it was always passed through).
    const documentH1 = document.querySelector('h1, [role="heading"][aria-level="1"]');
    let previousLevel = documentH1 ? 1 : null;
    let previousDeclaredLevel = documentH1 ? 1 : null;

    main.querySelectorAll(headingSelector).forEach((heading) => {
      if (!isExposed(heading)) return;

      const declaredLevel = getHeadingLevel(heading);
      if (declaredLevel === null) return;

      let accessibleLevel;
      if (previousDeclaredLevel !== null && declaredLevel === previousDeclaredLevel) {
        // Siblings that declare the same level must resolve to the same level.
        accessibleLevel = previousLevel;
      } else if (previousLevel !== null && declaredLevel > previousLevel + 1) {
        accessibleLevel = previousLevel + 1;
      } else {
        accessibleLevel = declaredLevel;
      }

      if (accessibleLevel !== declaredLevel) {
        heading.dataset.headingOrderAdjusted = 'true';
        heading.dataset.headingOrderOriginalRole = heading.getAttribute('role') ?? noValue;
        heading.dataset.headingOrderOriginalLevel = heading.getAttribute('aria-level') ?? noValue;
        heading.setAttribute('role', 'heading');
        heading.setAttribute('aria-level', String(accessibleLevel));
      }

      previousLevel = accessibleLevel;
      previousDeclaredLevel = declaredLevel;
    });
  }

  function scheduleNormalization() {
    window.cancelAnimationFrame(frameId);
    frameId = window.requestAnimationFrame(normalizeHeadingOrder);
  }

  scheduleNormalization();
  document.addEventListener('shopify:section:load', scheduleNormalization);
  document.addEventListener('toggle', scheduleNormalization, true);

  new MutationObserver(scheduleNormalization).observe(main, {
    childList: true,
    subtree: true,
    attributes: true,
    // Deliberately excludes 'style'/'class': several custom-liquid blocks mutate
    // inline styles on a 250ms interval, which would make this scan run forever.
    attributeFilter: ['aria-hidden', 'hidden', 'inert', 'open'],
  });
})();
